import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Plus, RefreshCw, Save, Search, Sparkles, X } from 'lucide-react';
import {
  BATCH_ACTION_MAX_TAGS,
  EXPRESS_MAX_SHIPPING_METHODS,
  EXPRESS_MAX_SHIPPING_VALUE_LENGTH,
  ExpressRuleSchema,
  type ExpressRule,
} from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

type ShippingPick = ExpressRule['shippingMethods'][number];
const EMPTY_RULE: ExpressRule = { tags: [], shippingMethods: [] };
const samePick = (a: ShippingPick, b: ShippingPick) => a.field === b.field && a.value === b.value;
const PAGE_SIZE = 50;

/** Own-store configuration. Discovery is optional; durable progress comes from the server. */
export function ExpressSettingsSection() {
  const { t } = useTranslation('settings');
  const { can } = useRole();
  const canManage = can('production_settings.manage');
  const utils = trpc.useUtils();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [inViewport, setInViewport] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState === 'visible');
  const visible = inViewport && pageVisible;
  useEffect(() => {
    const updateVisibility = () => setPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', updateVisibility);
    const observer = new IntersectionObserver(([entry]) => setInViewport(entry?.isIntersecting ?? false));
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, [canManage]);
  const [draft, setDraft] = useState<ExpressRule | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [manualField, setManualField] = useState<ShippingPick['field']>('title');
  const [manualValue, setManualValue] = useState('');
  const [shippingOpen, setShippingOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedSearch(search); setOffset(0); }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const settings = trpc.productionSettings.getExpressRule.useQuery(undefined, {
    enabled: canManage && visible,
    refetchInterval: (query) => visible ? ((query.state.data?.pendingCount ?? 0) > 0 ? 15_000 : 60_000) : false,
    refetchIntervalInBackground: false,
  });
  const coverage = trpc.productionSettings.getExpressShippingCoverage.useQuery(undefined, {
    enabled: canManage && visible,
    staleTime: 60_000,
    refetchInterval: visible ? 60_000 : false,
    refetchIntervalInBackground: false,
  });
  // Tag vocabulary is the same own-org allowlist used by Batch buttons.
  const batchSettings = trpc.productionSettings.getBatchActions.useQuery(undefined, { enabled: canManage });
  const observed = trpc.productionSettings.getObservedShippingMethods.useQuery({
    search: debouncedSearch, offset, limit: PAGE_SIZE,
  }, { enabled: canManage && shippingOpen });

  const refresh = async () => {
    await Promise.all([
      utils.productionSettings.getExpressRule.invalidate(),
      utils.productionSettings.getExpressShippingCoverage.invalidate(),
      utils.productionSettings.getBatchActions.invalidate(),
      utils.productionOrders.resolveBatchScopes.invalidate(),
      utils.productionOrders.getOrderQueue.invalidate(),
    ]);
  };
  const save = trpc.productionSettings.updateExpressRule.useMutation({
    onSuccess: async () => {
      // A save may finish after switching stores. Refetch the active store;
      // never put an old store's mutation response into the shared query key.
      await refresh();
      setDraft(null);
      toast.success(t('express.saved'));
    },
    onError: (error) => toast.error(t('express.saveError', { message: error.message }), { duration: Infinity }),
  });
  const retry = trpc.productionSettings.retryExpressRecompute.useMutation({
    onSuccess: async () => { await refresh(); },
    onError: (error) => toast.error(t('express.retryError', { message: error.message }), { duration: Infinity }),
  });

  const rule = draft ?? settings.data?.rule ?? EMPTY_RULE;
  const tags = batchSettings.data?.filterTags ?? [];
  const suggestedTags = ['Startrack-Premium', 'Express-Shipping', 'Express Shipping'].filter((tag) => tags.includes(tag));
  const suggestedUnion = [...new Set([...rule.tags, ...suggestedTags])];
  const changed = draft !== null && JSON.stringify(draft) !== JSON.stringify(settings.data?.rule);
  const valid = ExpressRuleSchema.safeParse(rule).success;
  const editingDisabled = !canManage || save.isPending;
  const addShipping = (pick: ShippingPick) => {
    if (!rule.shippingMethods.some((selected) => samePick(selected, pick)) && rule.shippingMethods.length < EXPRESS_MAX_SHIPPING_METHODS) {
      setDraft({ ...rule, shippingMethods: [...rule.shippingMethods, pick] });
    }
  };
  const manualPick = { field: manualField, value: manualValue.trim() };
  const manualValid = manualPick.value.length > 0 && manualPick.value.length <= EXPRESS_MAX_SHIPPING_VALUE_LENGTH;

  if (!canManage) return null;
  if (settings.isPending) return <div ref={sectionRef}><div className="space-y-3" role="status" aria-label={t('express.loading')}><Skeleton className="h-8 w-2/3" /><Skeleton className="h-24 w-full" /></div></div>;
  if (!settings.data) return (
    <div ref={sectionRef}><Alert variant="destructive"><AlertDescription>
      {t('express.loadError')}
      <Button variant="link" onClick={() => settings.refetch()}>{t('express.retry')}</Button>
    </AlertDescription></Alert></div>
  );

  return (
    <div ref={sectionRef} className="space-y-6">
      <p className="text-sm text-muted-foreground max-w-prose">{t('express.description')}</p>
      <p className="text-xs text-muted-foreground max-w-prose">{t('express.exactHelp')}</p>
      <div className="flex flex-wrap items-center gap-3" role="status" aria-live="polite">
        <span className="text-sm tabular-nums">
          {settings.isError ? t('express.statusError') : settings.data.pendingCount > 0
            ? t('express.pending', { count: settings.data.pendingCount }) : t('express.upToDate')}
        </span>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={retry.isPending || save.isPending}
          onClick={() => retry.mutate()}>
          {retry.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {t('express.retry')}
        </Button>
      </div>
      {(coverage.data?.unknownShippingCount ?? 0) > 0 && <Alert><AlertDescription>
        {t('express.unknownShipping', { count: coverage.data?.unknownShippingCount })}
      </AlertDescription></Alert>}

      <div className="space-y-3">
        <Label className="text-sm font-medium" htmlFor="express-tags">{t('express.tags')}</Label>
        <div className="flex flex-wrap gap-2">
          {rule.tags.map((tag) => <Badge key={tag} variant="secondary" className="max-w-full gap-1 whitespace-normal break-all font-normal">
            {tag}{batchSettings.data && !tags.includes(tag) && <span>{t('express.missingTag')}</span>}
            <Button variant="ghost" size="icon" className="h-6 w-6" disabled={editingDisabled}
              aria-label={t('express.remove', { value: tag })}
              onClick={() => setDraft({ ...rule, tags: rule.tags.filter((value) => value !== tag) })}><X className="h-3 w-3" /></Button>
          </Badge>)}
        </div>
        <Popover><PopoverTrigger asChild>
          <Button id="express-tags" variant="outline" className="h-8 gap-1.5" disabled={editingDisabled || rule.tags.length >= BATCH_ACTION_MAX_TAGS}>
            <Plus className="h-3.5 w-3.5" />{t('express.pickTags')}
          </Button>
        </PopoverTrigger><PopoverContent align="start" className="w-80 p-0"><Command>
          <CommandInput placeholder={t('express.searchTags')} />
          <CommandList><CommandEmpty>{batchSettings.isLoading ? t('express.loading') : batchSettings.isError ? t('express.tagsError') : t('express.noTags')}</CommandEmpty>
            <CommandGroup>{tags.map((tag) => <CommandItem key={tag} value={tag} disabled={editingDisabled || rule.tags.includes(tag) || rule.tags.length >= BATCH_ACTION_MAX_TAGS}
              onSelect={() => setDraft({ ...rule, tags: [...rule.tags, tag] })}>{tag}</CommandItem>)}</CommandGroup>
          </CommandList>
        </Command></PopoverContent></Popover>
        {batchSettings.isError && <p className="text-sm text-destructive">{t('express.tagsError')} <Button variant="link" onClick={() => batchSettings.refetch()}>{t('express.retry')}</Button></p>}
        {suggestedTags.length > 0 && <div className="space-y-1">
          <Button variant="outline" className="h-8 gap-1.5" disabled={editingDisabled || suggestedUnion.length > BATCH_ACTION_MAX_TAGS || suggestedTags.every((tag) => rule.tags.includes(tag))}
            onClick={() => setDraft({ ...rule, tags: suggestedUnion })}><Sparkles className="h-3.5 w-3.5" />{t('express.suggestTags')}</Button>
          <p className="text-xs text-muted-foreground">{suggestedTags.join(', ')}. {t('express.suggestionHelp')}</p>
        </div>}
      </div>

      <div className="space-y-3">
        <Label className="text-sm font-medium" htmlFor="express-shipping">{t('express.shippingMethods')}</Label>
        <p className="text-xs text-muted-foreground">{t('express.selectedHelp')}</p>
        <div className="flex flex-wrap gap-2">
          {rule.shippingMethods.map((pick) => <Badge key={JSON.stringify(pick)} variant="secondary" className="max-w-full gap-1 whitespace-normal break-all font-normal">
            {pick.field === 'title' ? t('express.titleField') : t('express.codeField')}: {pick.value}
            <Button variant="ghost" size="icon" className="h-6 w-6" disabled={editingDisabled}
              aria-label={t('express.remove', { value: pick.value })}
              onClick={() => setDraft({ ...rule, shippingMethods: rule.shippingMethods.filter((selected) => !samePick(selected, pick)) })}><X className="h-3 w-3" /></Button>
          </Badge>)}
        </div>
        <Popover open={shippingOpen} onOpenChange={setShippingOpen}><PopoverTrigger asChild>
          <Button id="express-shipping" variant="outline" className="h-8 gap-1.5" disabled={editingDisabled || rule.shippingMethods.length >= EXPRESS_MAX_SHIPPING_METHODS}>
            <Search className="h-3.5 w-3.5" />{t('express.pickShipping')}
          </Button>
        </PopoverTrigger><PopoverContent align="start" className="w-96 max-w-[90vw] p-0">
          <Command shouldFilter={false}>
            <CommandInput value={search} onValueChange={setSearch} placeholder={t('express.searchShipping')} />
            <CommandList>
              {observed.isFetching || search !== debouncedSearch ? <div className="p-3" role="status" aria-label={t('express.loading')}><Skeleton className="h-8 w-full" /></div>
                : observed.isError ? <p className="p-3 text-sm text-destructive">{t('express.discoveryError')}</p>
                : <><CommandEmpty>{t('express.noShipping')}</CommandEmpty><CommandGroup>
                  {observed.data?.methods.map((pick) => {
                    const selected = rule.shippingMethods.some((value) => samePick(value, pick));
                    return <CommandItem key={JSON.stringify([pick.field, pick.value])} value={JSON.stringify([pick.field, pick.value])}
                      disabled={editingDisabled || selected || rule.shippingMethods.length >= EXPRESS_MAX_SHIPPING_METHODS}
                      onSelect={() => addShipping({ field: pick.field, value: pick.value })}>
                      <span className="min-w-0 break-words">{pick.field === 'title' ? t('express.titleField') : t('express.codeField')}: {pick.value}{pick.field === 'code' && pick.title ? ` · ${pick.title}` : ''}</span>
                      {selected && <Check className="ml-auto h-3.5 w-3.5 shrink-0" />}
                    </CommandItem>;
                  })}
                </CommandGroup></>}
            </CommandList>
          </Command>
          <div className="flex items-center justify-between border-t p-2">
            <Button variant="ghost" size="sm" disabled={offset === 0 || observed.isFetching} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>{t('express.previous')}</Button>
            <span className="text-xs tabular-nums">{observed.data && !observed.isError && t('express.methodCount', { count: observed.data.total })}</span>
            <Button variant="ghost" size="sm" disabled={observed.isFetching || !observed.data || observed.isError || offset + PAGE_SIZE >= observed.data.total || offset >= 10000}
              onClick={() => setOffset(offset + PAGE_SIZE)}>{t('express.next')}</Button>
          </div>
        </PopoverContent></Popover>
        <div className="space-y-2 max-w-prose">
          <Label htmlFor="express-manual-value">{t('express.manualEntry')}</Label>
          <p className="text-xs text-muted-foreground">{t('express.manualHelp')}</p>
          <div className="flex flex-wrap gap-2">
            <Select value={manualField} onValueChange={(value) => setManualField(value as ShippingPick['field'])} disabled={editingDisabled}>
              <SelectTrigger className="w-32" aria-label={t('express.shippingField')}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="title">{t('express.titleField')}</SelectItem><SelectItem value="code">{t('express.codeField')}</SelectItem></SelectContent>
            </Select>
            <Input id="express-manual-value" className="flex-1 min-w-40" value={manualValue} onChange={(event) => setManualValue(event.target.value)}
              maxLength={EXPRESS_MAX_SHIPPING_VALUE_LENGTH} disabled={editingDisabled} />
            <Button variant="outline" disabled={editingDisabled || !manualValid || rule.shippingMethods.length >= EXPRESS_MAX_SHIPPING_METHODS || rule.shippingMethods.some((pick) => samePick(pick, manualPick))}
              onClick={() => { addShipping(manualPick); setManualValue(''); }}>{t('express.addMethod')}</Button>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t('express.limits', { tags: BATCH_ACTION_MAX_TAGS, methods: EXPRESS_MAX_SHIPPING_METHODS })}</p>
      {rule.tags.length === 0 && rule.shippingMethods.length === 0 && <p className="text-sm text-muted-foreground">{t('express.emptyRule')}</p>}
      {!valid && <p className="text-sm text-destructive">{t('express.invalidRule')}</p>}
      <div className="flex flex-wrap gap-2">
        <Button className="h-8 gap-1.5" disabled={!changed || !valid || editingDisabled} onClick={() => save.mutate({ rule })}>
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}{t('express.save')}
        </Button>
        <Button variant="outline" className="h-8" disabled={editingDisabled || (rule.tags.length === 0 && rule.shippingMethods.length === 0)} onClick={() => setDraft(EMPTY_RULE)}>{t('express.clear')}</Button>
      </div>
    </div>
  );
}
